import { cn } from "@/utils/utils";

interface SkeletonProps {
	className?: string;
}

function Skeleton({ className }: SkeletonProps) {
	return (
		<div
			className={cn("animate-pulse rounded bg-base-300", className)}
			aria-hidden="true"
		/>
	);
}

/** Skeleton for a dashboard card (icon + title + value) */
export function CardSkeleton({ className }: SkeletonProps) {
	return (
		<div
			className={cn(
				"rounded-xl border border-base-300 bg-base-100 p-6",
				className,
			)}
		>
			<div className="flex items-center gap-3 mb-4">
				<Skeleton className="h-10 w-10 rounded-lg" />
				<Skeleton className="h-4 w-24" />
			</div>
			<Skeleton className="h-8 w-16 mb-2" />
			<Skeleton className="h-3 w-32" />
		</div>
	);
}

/** Skeleton for a dashboard page with cards grid */
export function DashboardSkeleton() {
	return (
		<output className="block space-y-6" aria-label="Loading dashboard">
			{/* Header */}
			<div className="space-y-2">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-4 w-72" />
			</div>
			{/* Cards grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
				<CardSkeleton />
				<CardSkeleton />
				<CardSkeleton />
			</div>
			{/* Content area */}
			<div className="rounded-xl border border-base-300 bg-base-100 p-6">
				<Skeleton className="h-5 w-32 mb-4" />
				<div className="space-y-3">
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-5/6" />
				</div>
			</div>
		</output>
	);
}

/** Skeleton for a detail page (header + sections) */
export function DetailSkeleton() {
	return (
		<output className="block space-y-6" aria-label="Loading details">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Skeleton className="h-6 w-6" />
				<div className="space-y-2 flex-1">
					<Skeleton className="h-7 w-48" />
					<Skeleton className="h-4 w-64" />
				</div>
			</div>
			{/* Info cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div className="rounded-xl border border-base-300 bg-base-100 p-5 space-y-3">
					<Skeleton className="h-5 w-24" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-5/6" />
				</div>
				<div className="rounded-xl border border-base-300 bg-base-100 p-5 space-y-3">
					<Skeleton className="h-5 w-24" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-5/6" />
				</div>
			</div>
		</output>
	);
}

/** Skeleton for a form page */
export function FormSkeleton() {
	return (
		<output className="block space-y-6" aria-label="Loading form">
			{/* Header */}
			<div className="space-y-2">
				<Skeleton className="h-7 w-40" />
				<Skeleton className="h-4 w-56" />
			</div>
			{/* Form fields */}
			<div className="rounded-xl border border-base-300 bg-base-100 p-6 space-y-5">
				{[1, 2, 3].map((i) => (
					<div key={i} className="space-y-2">
						<Skeleton className="h-4 w-20" />
						<Skeleton className="h-10 w-full" />
					</div>
				))}
				<div className="flex justify-end gap-3 pt-4">
					<Skeleton className="h-10 w-24" />
					<Skeleton className="h-10 w-24" />
				</div>
			</div>
		</output>
	);
}

export { Skeleton };
